import PageMeta from "@/components/PageMeta";
import { useCallback, useMemo } from "react";
import {
  ArrowRight,
  Grid3X3,
  Layers,
  Paintbrush2,
  Sparkles,
  Tag,
  ZoomIn,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import SectionFadeIn from "@/components/SectionFadeIn";
import { getRouteState } from "@/lib/navigation";
import { FloorConfigurator } from "@/components/floor-graphics/FloorConfigurator";

/* ── Pricing (shared with FloorConfigurator for display prices) ── */

const MATERIAL_RATES = {
  vinyl: { perSqm: 9, min: 12 },
  "anti-slip": { perSqm: 14, min: 18 },
  wrap: { perSqm: 18, min: 25 },
};

const QUANTITY_TIERS = [
  { min: 1, max: 4, mult: 1.0 },
  { min: 5, max: 9, mult: 0.92 },
  { min: 10, max: 24, mult: 0.85 },
  { min: 25, max: 49, mult: 0.78 },
  { min: 50, max: 99, mult: 0.72 },
  { min: 100, max: 9999, mult: 0.65 },
];

function calcFloorPrice({ widthM, heightM, materialId, quantity }) {
  const area = widthM * heightM;
  if (area <= 0) return null;
  const rate = MATERIAL_RATES[materialId] || MATERIAL_RATES.vinyl;
  const rawUnit = Math.max(rate.min, area * rate.perSqm);
  const tier = QUANTITY_TIERS.find((t) => quantity >= t.min && quantity <= t.max) || QUANTITY_TIERS[0];
  const unitPrice = rawUnit * tier.mult;
  return {
    unitPrice: Math.ceil(unitPrice * 100) / 100,
    totalPrice: Math.ceil(unitPrice * quantity * 100) / 100,
    area,
    perSqm: unitPrice / area,
    discount: Math.round((1 - tier.mult) * 100),
  };
}

function formatGBP(n) {
  if (n == null || isNaN(n)) return "";
  return `\u00a3${n.toFixed(2)}`;
}

/* ── Static data ── */

const MATERIALS = [
  { id: "vinyl", title: "Vinyl Floor Sticker", short: "Indoor events & temporary display", description: "Removable calendared vinyl. Clear edges, full-colour print. Ideal for events, retail promotions, and seasonal floor graphics.", icon: Grid3X3, image: "https://images.pexels.com/photos/1267338/pexels-photo-1267338.jpeg?auto=compress&cs=tinysrgb&w=600&q=80" },
  { id: "anti-slip", title: "Anti-Slip Floor Print", short: "High-traffic, safety-certified", description: "Textured anti-slip laminate over a full-colour floor print. Meets UK slip-resistance requirements for retail floors, warehouses, and food-service areas.", icon: Layers, image: "https://images.pexels.com/photos/6153354/pexels-photo-6153354.jpeg?auto=compress&cs=tinysrgb&w=600&q=80" },
  { id: "wrap", title: "Floor Wrap", short: "Full-area coverage, showrooms & exhibitions", description: "Large-format floor coverage for car showrooms, exhibition stands, and branded retail environments. Durable cast vinyl with matt or gloss finish.", icon: Paintbrush2, image: "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=600&q=80" },
];

const PRICE_GUIDE = [
  { label: "A3 Vinyl Floor Sticker", desc: "29.7\u00d742 cm, qty 1", wM: 0.297, hM: 0.42, matId: "vinyl", qty: 1 },
  { label: "A2 Vinyl Floor Sticker", desc: "42\u00d759.4 cm, qty 10", wM: 0.42, hM: 0.594, matId: "vinyl", qty: 10 },
  { label: "A1 Anti-Slip Print", desc: "59.4\u00d784.1 cm, qty 5", wM: 0.594, hM: 0.841, matId: "anti-slip", qty: 5 },
  { label: "1 \u00d7 1 m Floor Wrap", desc: "1\u00d71 m, qty 1", wM: 1, hM: 1, matId: "wrap", qty: 1 },
  { label: "2 \u00d7 1 m Vinyl Runner", desc: "2\u00d71 m, qty 3", wM: 2, hM: 1, matId: "vinyl", qty: 3 },
  { label: "3 \u00d7 2 m Showroom Wrap", desc: "3\u00d72 m, qty 1", wM: 3, hM: 2, matId: "wrap", qty: 1 },
];

const GENERIC_QUOTE_HREF = "/request-quote?type=Business%20Signage&description=Floor%20graphic%20enquiry.";

/* ── Page ── */

export default function FloorGraphicsPage() {
  const location = useLocation();

  const calc = useCallback((opts) => calcFloorPrice(opts), []);

  const heroStartingPrice = useMemo(
    () => calc({ widthM: 0.297, heightM: 0.42, materialId: "vinyl", quantity: 1 })?.unitPrice || 12,
    [calc]
  );

  const guidePrices = useMemo(
    () => PRICE_GUIDE.map((g) => calc({ widthM: g.wM, heightM: g.hM, materialId: g.matId, quantity: g.qty })),
    [calc]
  );

  return (
    <div className="py-16 md:py-24 pb-28 md:pb-24" data-testid="floor-graphics-page">
      <PageMeta
        title="Floor Graphics & Floor Prints | AM Decor"
        description="Custom vinyl floor stickers, anti-slip floor prints, and large-format floor wraps for retail, events, showrooms, and commercial spaces. Full-colour, made to measure."
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* ── Hero ── */}
        <SectionFadeIn>
          <section className="rounded-sm border border-brand-border bg-[radial-gradient(circle_at_top,_rgba(203,160,82,0.12),_transparent_32%),linear-gradient(135deg,rgba(255,255,255,0.03),rgba(10,13,20,0.96))] px-6 py-14 md:px-10 md:py-16" data-testid="floor-hero">
            <div className="grid grid-cols-1 lg:grid-cols-[1.08fr_0.92fr] gap-10 items-start">
              <div>
                <p className="text-[11px] tracking-[0.28em] uppercase font-semibold text-brand-gold">Floor Graphics & Floor Prints</p>
                <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] text-brand-text max-w-3xl" data-testid="floor-heading">Make the floor part of the brand</h1>
                <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-brand-text-secondary" data-testid="floor-hero-copy">
                  Vinyl floor stickers, anti-slip prints, and large-format floor wraps — designed, printed, and finished in-house for retail, events, car showrooms, warehouses, and commercial spaces.
                </p>
                <div className="mt-5 inline-flex items-center gap-3 rounded-sm border border-brand-gold/25 bg-brand-gold/[0.06] px-5 py-3" data-testid="floor-hero-price">
                  <Tag size={16} className="text-brand-gold shrink-0" />
                  <span className="text-[14px] font-semibold text-brand-text">Floor stickers from <span className="text-brand-gold">{formatGBP(heroStartingPrice)}</span></span>
                  <span className="text-[11px] text-brand-text-secondary/50">per unit</span>
                </div>
                <div className="mt-5 flex flex-wrap gap-2" data-testid="floor-service-tags">
                  {["Vinyl Floor Stickers", "Anti-Slip Prints", "Floor Wraps", "Retail & Events", "Showroom Graphics", "Safety Zones"].map((tag) => (
                    <span key={tag} className="rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-[10px] uppercase tracking-[0.14em] text-brand-text-secondary/70">{tag}</span>
                  ))}
                </div>
                <div className="mt-8 flex flex-wrap gap-3" data-testid="floor-hero-ctas">
                  <Link to={GENERIC_QUOTE_HREF} state={getRouteState(location)} className="inline-flex items-center gap-2 rounded-sm bg-brand-gold px-7 py-3.5 text-[13px] font-semibold tracking-wide text-brand-bg transition-colors duration-300 hover:bg-brand-gold-hover" data-testid="floor-hero-quote-cta">
                    Request a Quote <ArrowRight size={15} />
                  </Link>
                  <button type="button" onClick={() => document.getElementById("floor-configurator-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" })} className="inline-flex items-center gap-2 rounded-sm border border-white/20 px-7 py-3.5 text-[13px] font-medium tracking-wide text-brand-text transition-colors duration-300 hover:border-brand-gold hover:text-brand-gold" data-testid="floor-hero-configure-cta">
                    Configure &amp; Price <ArrowRight size={15} />
                  </button>
                </div>
              </div>
              <div className="rounded-sm border border-white/[0.06] bg-brand-bg/70 p-5" data-testid="floor-trust-section">
                <p className="text-[11px] tracking-[0.18em] uppercase font-semibold text-brand-gold">Why AM Decor</p>
                <div className="mt-4 space-y-3">
                  {[
                    { icon: ZoomIn, title: "Full-colour, sharp to the edge", copy: "Roland TrueVIS VG-640 print and contour-cut. No colour banding, no dull edges." },
                    { icon: Layers, title: "Anti-slip certified", copy: "Our textured anti-slip laminate meets UK slip-resistance standards for public and commercial floors." },
                    { icon: Sparkles, title: "Removable or permanent", copy: "Removable calendared vinyl for events; cast vinyl for long-term showroom and retail installs." },
                  ].map((item) => (
                    <div key={item.title} className="rounded-sm border border-white/[0.05] bg-brand-surface/40 px-4 py-4">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 text-brand-gold"><item.icon size={18} /></span>
                        <div>
                          <p className="text-[13px] font-medium text-brand-text">{item.title}</p>
                          <p className="mt-1 text-[12px] leading-relaxed text-brand-text-secondary/65">{item.copy}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </SectionFadeIn>

        {/* ── Material showcase ── */}
        <SectionFadeIn>
          <section className="mt-16" data-testid="floor-materials-section">
            <div className="max-w-2xl mb-8">
              <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-gold">Floor graphic types</p>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-brand-text">Three materials, one print system</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {MATERIALS.map((mat, index) => (
                <SectionFadeIn key={mat.id} delay={index * 70}>
                  <button type="button" onClick={() => document.getElementById("floor-configurator-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" })} className="group h-full w-full overflow-hidden rounded-sm border border-brand-border bg-brand-surface/40 text-left transition-[transform,border-color] duration-300 hover:-translate-y-0.5 hover:border-brand-gold/40" data-testid={`floor-material-card-${mat.id}`}>
                    <div className="relative aspect-[16/9] overflow-hidden bg-brand-bg">
                      <img src={mat.image} alt={mat.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14]/80 via-[#0A0D14]/15 to-transparent" />
                      <div className="absolute bottom-3 right-3 rounded-sm bg-brand-bg/85 backdrop-blur-sm border border-brand-gold/30 px-3 py-1.5">
                        <span className="text-[13px] font-bold text-brand-gold">
                          from {formatGBP(calcFloorPrice({ widthM: 0.594, heightM: 0.841, materialId: mat.id, quantity: 1 })?.unitPrice || 0)}
                        </span>
                      </div>
                    </div>
                    <div className="px-5 py-5">
                      <div className="flex items-center gap-2 text-brand-gold"><mat.icon size={18} /><span className="text-[10px] uppercase tracking-[0.18em]">Floor material</span></div>
                      <h3 className="mt-4 text-[18px] font-medium text-brand-text group-hover:text-brand-gold transition-colors duration-300">{mat.title}</h3>
                      <p className="mt-2 text-[13px] leading-relaxed text-brand-text-secondary">{mat.description}</p>
                      <p className="mt-3 text-[11px] uppercase tracking-[0.16em] text-brand-gold/80">Configure this <ArrowRight size={12} className="inline" /></p>
                    </div>
                  </button>
                </SectionFadeIn>
              ))}
            </div>
          </section>
        </SectionFadeIn>

        {/* ── Price guide ── */}
        <SectionFadeIn>
          <section className="mt-16" data-testid="floor-price-guide">
            <div className="max-w-2xl mb-8">
              <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-gold">Quick price guide</p>
              <h2 className="mt-3 font-heading text-2xl sm:text-3xl tracking-tight text-brand-text">Representative floor graphic prices</h2>
              <p className="mt-2 text-[13px] text-brand-text-secondary/60">Single units. Volume discounts apply at 5+, 10+, 25+, and 50+.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="floor-price-guide-table">
                <thead>
                  <tr className="border-b border-white/[0.08]">
                    <th className="py-3 pr-4 text-[11px] uppercase tracking-[0.14em] font-semibold text-brand-text-secondary/60">Product</th>
                    <th className="py-3 pr-4 text-[11px] uppercase tracking-[0.14em] font-semibold text-brand-text-secondary/60 hidden sm:table-cell">Spec</th>
                    <th className="py-3 text-right text-[11px] uppercase tracking-[0.14em] font-semibold text-brand-text-secondary/60">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {PRICE_GUIDE.map((ex, i) => (
                    <tr key={ex.label} className="border-b border-white/[0.04] last:border-0">
                      <td className="py-3.5 pr-4">
                        <span className="text-[13px] font-medium text-brand-text">{ex.label}</span>
                        <span className="block sm:hidden mt-0.5 text-[11px] text-brand-text-secondary/50">{ex.desc}</span>
                      </td>
                      <td className="py-3.5 pr-4 text-[12px] text-brand-text-secondary/55 hidden sm:table-cell">{ex.desc}</td>
                      <td className="py-3.5 text-right">
                        <span className="text-[15px] font-bold text-brand-gold" data-testid={`floor-guide-price-${i}`}>{formatGBP(guidePrices[i]?.unitPrice || 0)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-[11px] text-brand-text-secondary/40">Prices include print and standard laminate. Design, installation, and artwork prep quoted separately where applicable.</p>
          </section>
        </SectionFadeIn>

        {/* ── Configurator ── */}
        <div id="floor-configurator-anchor">
          <SectionFadeIn>
            <FloorConfigurator />
          </SectionFadeIn>
        </div>

        {/* ── Final CTA ── */}
        <SectionFadeIn>
          <section className="mt-16 rounded-sm border border-white/[0.05] bg-brand-surface/40 px-6 py-12 md:px-8" data-testid="floor-final-cta">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-brand-text">Retail. Events. Showrooms. Safety zones.</h2>
                <p className="mt-2 text-[14px] leading-relaxed text-brand-text-secondary max-w-2xl">
                  Tell us the floor area, the surface type, and how long it needs to last. We'll match you to the right material and turn the quote around fast.
                </p>
              </div>
              <Link to={GENERIC_QUOTE_HREF} state={getRouteState(location)} className="inline-flex items-center justify-center gap-2 rounded-sm bg-brand-gold px-8 py-3.5 text-[13px] font-semibold tracking-wide text-brand-bg transition-colors duration-300 hover:bg-brand-gold-hover shrink-0" data-testid="floor-final-cta-button">
                Get Your Quote Now <ArrowRight size={15} />
              </Link>
            </div>
          </section>
        </SectionFadeIn>
      </div>
    </div>
  );
}
